import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useGridFilterDisplay } from 'ag-grid-react';
import { Checkbox, Empty, Input } from 'antd';
import { getLang } from 'shared/store/selectors/lang.selector';
import { sortByAlphabet } from 'shared/utils';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import styles from './set-filter.module.scss';
import { Button, Divider } from 'src/components/design-system';
import { colorPalette } from 'shared/constants';
import { useFocusTrap } from 'src/components/common/items-navigator';

/**
 * Check if a value is considered blank/empty
 */
const isBlankValue = value =>
  value == null || value === '' || (Array.isArray(value) && value.length === 0);

/**
 * Default identity function for valueFormatter (defined outside component for stability)
 */
const defaultValueFormatter = v => v;

/**
 * Filter logic for SetFilter - used at column definition level
 * @param {import('ag-grid-community').DoesFilterPassParams} params
 */
export const setFilterDoesFilterPass = ({ model, node, handlerParams }) => {
  // No filter active
  if (!model) {
    return true;
  }

  const hasValues = model.values && model.values.length > 0;
  const filterBlanks = model.filterBlanks;

  // No filter criteria set
  if (!hasValues && !filterBlanks) {
    return true;
  }

  const cellValue = handlerParams.getValue(node);

  // Check if filtering for blanks and cell is blank
  if (filterBlanks && isBlankValue(cellValue)) {
    return true;
  }

  // If only filtering blanks (no values selected), non-blank cells don't pass
  if (!hasValues) {
    return false;
  }

  const selectedSet = new Set(model.values);

  // Handle array values (e.g., specialty, division)
  if (Array.isArray(cellValue)) {
    return cellValue.some(v => selectedSet.has(v));
  }

  return selectedSet.has(cellValue);
};

/**
 * Custom AG Grid Set Filter component (Community Edition alternative to agSetColumnFilter)
 * Supports single values and arrays (e.g., specialty, division)
 * Supports predefined options via filterParams.options for server-side filtering
 * @param {import('ag-grid-react').CustomFilterDisplayProps & { valueFormatter?: (value: string) => string, options?: Array<{ text: string, value: any }>, applyMode?: boolean }} props
 */
export function SetFilter({
  api,
  model,
  onModelChange,
  getValue,
  valueFormatter,
  options,
  applyMode = false,
}) {
  // Use stable default for valueFormatter
  const stableValueFormatter = valueFormatter || defaultValueFormatter;
  const lang = useSelector(getLang('FILTERS'));
  const [searchText, setSearchText] = useState('');
  const [allValues, setAllValues] = useState([]);
  const [hasBlanks, setHasBlanks] = useState(false);
  const searchInputRef = useRef(null);
  const hidePopupRef = useRef(null);
  const triggerRef = useRef(null);

  // Local state for pending selections (used when applyMode is true)
  const [pendingValues, setPendingValues] = useState(new Set());
  const [pendingBlanks, setPendingBlanks] = useState(false);

  // Initialize pending state from model when applyMode changes or model updates externally
  useEffect(() => {
    if (applyMode) {
      setPendingValues(model?.values ? new Set(model.values) : new Set());
      setPendingBlanks(model?.filterBlanks ?? false);
    }
  }, [applyMode, model]);

  const afterGuiAttached = useCallback(params => {
    hidePopupRef.current = params?.hidePopup ?? null;
    // Capture the element that opened the popup so we can restore focus when it closes
    triggerRef.current = document.activeElement;
    if (!params || !params.suppressFocus) {
      // Focus the input element for keyboard navigation.
      // Can't do this in an effect, as the component is not recreated when hidden and then shown again
      searchInputRef.current?.focus();
    }
  }, []);

  const afterGuiDetached = useCallback(() => {
    hidePopupRef.current = null;
    const trigger = triggerRef.current;
    triggerRef.current = null;
    // Restore focus to the trigger element only if AG Grid didn't already restore it
    // (Escape key restores focus natively; button clicks cause focus to fall to body)
    requestAnimationFrame(() => {
      if (
        trigger &&
        document.contains(trigger) &&
        (!document.activeElement || document.activeElement === document.body)
      ) {
        trigger.focus();
      }
    });
  }, []);

  // Register lifecycle callbacks with AG Grid
  useGridFilterDisplay({
    afterGuiAttached,
    afterGuiDetached,
  });

  // Serialize options to a stable string key for dependency comparison
  // This prevents infinite loops when options array reference changes but content is the same
  // Sort the values to make the key order-independent (since parent may sort options differently)
  const optionsKey = useMemo(
    () => (options ? JSON.stringify([...options.map(o => o.value)].sort()) : ''),
    [options],
  );

  // Track whether we have predefined options
  const hasPredefinedOptions = options && options.length > 0;

  // Effect for predefined options (server-side filtering) - only depends on optionsKey
  useEffect(() => {
    if (!hasPredefinedOptions) return;
    const values = options.map(opt => opt.value);
    setAllValues(values);
    setHasBlanks(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- optionsKey derives from options, so options is redundant
  }, [hasPredefinedOptions, optionsKey]);

  // Effect for extracting values from column data (client-side) - only runs when no predefined options
  useEffect(() => {
    if (hasPredefinedOptions) return;

    // Extract unique values from column data (supports arrays)
    const uniqueValues = new Set();
    let foundBlanks = false;

    api.forEachNode(node => {
      const cellValue = getValue(node);
      if (isBlankValue(cellValue)) {
        foundBlanks = true;
      } else if (Array.isArray(cellValue)) {
        cellValue.forEach(v => v !== null && v !== '' && uniqueValues.add(v));
      } else {
        uniqueValues.add(cellValue);
      }
    });

    const sorted = Array.from(uniqueValues).sort((a, b) =>
      sortByAlphabet(stableValueFormatter(a), stableValueFormatter(b)),
    );
    setAllValues(sorted);
    setHasBlanks(foundBlanks);
  }, [api, getValue, stableValueFormatter, hasPredefinedOptions]);

  // Create valueFormatter that handles predefined options
  const getDisplayValue = useCallback(
    val => {
      if (options && options.length > 0) {
        const opt = options.find(o => o.value === val);
        return opt?.text || val;
      }
      return stableValueFormatter(val);
    },
    [options, stableValueFormatter],
  );

  // Determine checked state from model (or pending state in applyMode)
  const checkedValues = useMemo(() => {
    if (applyMode) return pendingValues;
    if (!model || !model.values) return new Set();
    return new Set(model.values);
  }, [model, applyMode, pendingValues]);

  const isBlanksChecked = applyMode ? pendingBlanks : (model?.filterBlanks ?? false);

  // Filter options by search text (search in formatted display value)
  const displayedOptions = useMemo(() => {
    if (!searchText.trim()) return allValues;

    const search = searchText.toLowerCase();

    return allValues.filter(val => getDisplayValue(val).toLowerCase().includes(search));
  }, [allValues, searchText, getDisplayValue]);

  // Toggle single value
  const onCheck = useCallback(
    value => {
      if (applyMode) {
        // Update pending state only
        setPendingValues(prev => {
          const newSet = new Set(prev);
          if (newSet.has(value)) {
            newSet.delete(value);
          } else {
            newSet.add(value);
          }
          return newSet;
        });
      } else {
        // Immediate mode - apply directly
        const newSet = new Set(checkedValues);
        if (newSet.has(value)) {
          newSet.delete(value);
        } else {
          newSet.add(value);
        }
        const hasValues = newSet.size > 0 || isBlanksChecked;
        onModelChange(
          hasValues ? { values: Array.from(newSet), filterBlanks: isBlanksChecked } : null,
        );
      }
    },
    [applyMode, checkedValues, onModelChange, isBlanksChecked],
  );

  // Toggle blanks filter
  const onCheckBlanks = useCallback(() => {
    if (applyMode) {
      setPendingBlanks(prev => !prev);
    } else {
      const newFilterBlanks = !isBlanksChecked;
      const hasValues = checkedValues.size > 0 || newFilterBlanks;
      onModelChange(
        hasValues ? { values: Array.from(checkedValues), filterBlanks: newFilterBlanks } : null,
      );
    }
  }, [applyMode, checkedValues, onModelChange, isBlanksChecked]);

  // Apply pending selections (for applyMode)
  const onApply = useCallback(() => {
    const hasValues = pendingValues.size > 0 || pendingBlanks;
    onModelChange(
      hasValues ? { values: Array.from(pendingValues), filterBlanks: pendingBlanks } : null,
    );
    hidePopupRef.current?.();
  }, [pendingValues, pendingBlanks, onModelChange]);

  // Reset filter - clear all selections
  const onReset = useCallback(() => {
    if (applyMode) {
      setPendingValues(new Set());
      setPendingBlanks(false);
    }
    onModelChange(null);
    hidePopupRef.current?.();
  }, [applyMode, onModelChange]);

  const hasSelection = checkedValues.size > 0 || isBlanksChecked;

  // Check if pending state differs from applied state (for showing Apply button state)
  const hasPendingChanges = useMemo(() => {
    if (!applyMode) return false;
    const currentValues = model?.values ? new Set(model.values) : new Set();
    const currentBlanks = model?.filterBlanks ?? false;

    if (pendingValues.size !== currentValues.size) return true;
    if (pendingBlanks !== currentBlanks) return true;
    for (const v of pendingValues) {
      if (!currentValues.has(v)) return true;
    }
    return false;
  }, [applyMode, model, pendingValues, pendingBlanks]);

  const { rootRef } = useFocusTrap();

  // Check if blanks option should be shown (matches search)
  const showBlanksOption = useMemo(() => {
    if (!hasBlanks) return false;
    if (!searchText.trim()) return true;
    return lang.BLANKS.toLowerCase().includes(searchText.toLowerCase());
  }, [hasBlanks, searchText, lang.BLANKS]);

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.header}>
        <Input
          type="search"
          placeholder={lang.SEARCH_PLACEHOLDER}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          ref={searchInputRef}
          className={styles.search}
        />
        <div className={styles.headerActions}>
          <Button
            className={styles.buttonAction}
            size="small"
            onClick={onReset}
            disabled={!hasSelection && !hasPendingChanges}>
            {lang.RESET}
          </Button>
          {applyMode && (
            <Button
              className={styles.buttonAction}
              size="small"
              type="primary"
              onClick={onApply}
              disabled={!hasPendingChanges}>
              {lang.APPLY || 'Apply'}
            </Button>
          )}
        </div>
      </div>

      <Divider dividerColor={colorPalette.neutral4} />

      {displayedOptions.length > 0 || showBlanksOption ? (
        <ListItemsNavigator className={styles.options} itemOverrides={{ role: 'checkbox' }}>
          {displayedOptions.map(val => (
            <Checkbox
              key={val}
              checked={checkedValues.has(val)}
              onChange={() => onCheck(val)}
              classNames={{
                root: styles.checkbox,
                label: styles.checkboxLabel,
              }}>
              {getDisplayValue(val)}
            </Checkbox>
          ))}
          {showBlanksOption && (
            <Checkbox
              key="__blanks__"
              checked={isBlanksChecked}
              onChange={onCheckBlanks}
              classNames={{
                root: styles.checkbox,
                label: styles.checkboxLabel,
              }}>
              <em className={styles.emptyValue}>{lang.BLANKS}</em>
            </Checkbox>
          )}
        </ListItemsNavigator>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={lang.NO_RESULTS} />
      )}
    </div>
  );
}
